import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import {
  UserPlus, UserMinus, MessageCircle, Edit, MapPin,
  Ruler, Weight as WeightIcon, Users, Play, Store,
  Trash2, Lock, Eye, Trophy, Shield, Heart, Quote,
  Package, LogOut, ArrowLeft, Camera, ChevronRight
} from 'lucide-react';
import { cn } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import { socialAPI, playOfWeekAPI, marketplaceAPI } from '../services/api';
import { useToast } from '../hooks/use-toast';
import ConfirmDialog from '../components/ConfirmDialog';
import PostCard from '../components/PostCard';

import UserListModal from '../components/UserListModal';

// Tabs Component
const Tabs = ({ activeTab, onTabChange, profile }) => {
  const { t } = useTranslation();
  const tabs = profile.membership_type === 'seller' ? ['products'] : ['highlights', 'feed'];

  return (
    <div className="flex bg-black/40 backdrop-blur-md p-1.5 rounded-[1.5rem] border border-white/5 mb-8 w-full max-w-sm mx-auto shadow-xl ring-1 ring-white/5 overflow-x-auto scrollbar-hide">
      {tabs.map((tab) => (
        <button
          key={tab}
          onClick={() => onTabChange(tab)}
          className={cn(
            "flex-1 py-3 px-4 text-sm font-black capitalize rounded-[1.2rem] transition-all duration-300 relative overflow-hidden whitespace-nowrap",
            activeTab === tab
              ? "text-white bg-gradient-to-br from-snapp-purple to-snapp-blue shadow-lg shadow-snapp-purple/20"
              : "text-gray-500 hover:text-gray-300"
          )}
        >
          <div className="relative z-10">
            {tab === 'products' ? (
              <><Store className="w-3.5 h-3.5 inline-block mr-1.5 mb-0.5" />{t('marketplace.products')}</>
            ) : t(`profile.${tab}`)}
          </div>
          {activeTab === tab && (
            <div className="absolute inset-0 bg-white/10 animate-in fade-in zoom-in duration-500" />
          )}
        </button>
      ))}
    </div>
  );
};

const Profile = () => {
  const { username } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const { toast } = useToast();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [followLoading, setFollowLoading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, postId: null });
  const [activeTab, setActiveTab] = useState('highlights'); // 'highlights' or 'feed' or 'products'
  const [userListings, setUserListings] = useState([]);
  const [userListModal, setUserListModal] = useState({ isOpen: false, title: '', users: [], loading: false });

  const isOwnProfile = user?.username === username || (profile && profile.user_id === user?.id);

  useEffect(() => {
    loadProfile();
  }, [username]);

  const loadProfile = async () => {
    try {
      setLoading(true);
      const response = await socialAPI.getProfile(username);
      setProfile(response.data);
      // If seller, load their listings and default to products tab
      if (response.data.membership_type === 'seller') {
        setActiveTab('products');
        try {
          const listingsRes = await marketplaceAPI.getUserListings(username);
          setUserListings(listingsRes.data.listings || []);
        } catch (e) { console.error('Failed to load listings', e); }
      }
    } catch (error) {
      toast({ title: t('common.error'), description: t('profile.notFound'), variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleLike = async (postId) => {
    try {
      const response = await socialAPI.toggleLike(postId);
      setProfile(prev => ({
        ...prev,
        posts: (prev.posts || []).map(p =>
          p.id === postId ? { ...p, is_liked: response.data.liked, like_count: response.data.like_count } : p
        )
      }));
    } catch (error) {
      toast({ title: t('common.error'), variant: 'destructive' });
    }
  };

  const handleComment = async (postId, text) => {
    try {
      const response = await socialAPI.addComment(postId, text);
      setProfile(prev => ({
        ...prev,
        posts: (prev.posts || []).map(p =>
          p.id === postId ? { ...p, comment_count: p.comment_count + 1 } : p
        )
      }));
      return response.data;
    } catch (error) {
      toast({ title: t('common.error'), variant: 'destructive' });
      return null;
    }
  };

  const handleDeleteComment = async (commentId) => {
    try {
      await socialAPI.deleteComment(commentId);
      return true;
    } catch (error) {
      toast({ title: t('common.error'), description: 'Failed to delete comment', variant: 'destructive' });
      return false;
    }
  };

  const handleVote = async (postId) => {
    try {
      const response = await playOfWeekAPI.vote(postId);
      if (response.data.status === 'success') {
        setProfile(prev => ({
          ...prev,
          posts: (prev.posts || []).map(p =>
            p.id === postId
              ? { ...p, vote_count: response.data.vote_count, user_can_vote: false }
              : { ...p, user_can_vote: false }
          )
        }));
        toast({ title: t('common.success'), description: t('feed.voteRecorded') });
      }
    } catch (error) {
      toast({
        title: t('common.error'),
        description: error.response?.data?.detail || t('feed.voteError'),
        variant: 'destructive'
      });
    }
  };

  const handleDeletePost = (postId) => {
    setDeleteConfirm({ isOpen: true, postId });
  };

  const confirmDelete = async () => {
    const postId = deleteConfirm.postId;
    if (!postId) return;

    try {
      await socialAPI.deletePost(postId);
      setProfile(prev => ({
        ...prev,
        posts: (prev.posts || []).filter(p => p.id !== postId)
      }));
      toast({ title: t('common.success'), description: t('profile.highlightDeleted') });
    } catch (error) {
      toast({ title: t('common.error'), variant: 'destructive' });
    } finally {
      setDeleteConfirm({ isOpen: false, postId: null });
    }
  };

  const handleEditPost = async (postId, data) => {
    try {
      const response = await socialAPI.updatePost(postId, data);
      setProfile(prev => ({
        ...prev,
        posts: (prev.posts || []).map(p => p.id === postId ? response.data : p)
      }));
      toast({ title: t('common.success'), description: t('feed.postUpdated') });
      return true;
    } catch (error) {
      toast({ title: t('common.error'), description: t('feed.postUpdateError'), variant: 'destructive' });
      return false;
    }
  };

  const handleFeaturePost = async (postId) => {
    try {
      const response = await socialAPI.toggleFeature(postId);
      setProfile(prev => ({
        ...prev,
        posts: (prev.posts || []).map(p =>
          p.id === postId ? { ...p, is_featured: response.data.is_featured } : p
        )
      }));
      toast({
        title: t('common.success'),
        description: response.data.is_featured
          ? (t('feed.addedToHighlights') || 'Öne çıkanlara eklendi')
          : (t('feed.removedFromHighlights') || 'Öne çıkanlardan kaldırıldı')
      });
    } catch (error) {
      toast({ title: t('common.error'), variant: 'destructive' });
    }
  };

  const handleReportPost = async (postId) => {
    try {
      await socialAPI.reportPost(postId, 'Reported from profile');
      toast({ title: t('common.success'), description: t('feed.reportSuccess') });
    } catch (error) {
      toast({ title: t('common.error'), variant: 'destructive' });
    }
  };

  const handleFollow = async () => {
    if (followLoading) return;
    try {
      setFollowLoading(true);
      const response = await socialAPI.toggleFollow(username);
      setProfile(prev => ({
        ...prev,
        is_followed_by_me: response.data.following,
        follow_status: response.data.status,
        follower_count: response.data.follower_count
      }));
    } catch (error) {
      toast({ title: t('common.error'), variant: 'destructive' });
    } finally {
      setFollowLoading(false);
    }
  };

  const handleShowFollowers = async () => {
    setUserListModal({ isOpen: true, title: t('profile.followers'), users: [], loading: true });
    try {
      const response = await socialAPI.getFollowers(username);
      setUserListModal(prev => ({ ...prev, users: response.data.users, loading: false }));
    } catch (error) {
      toast({ title: t('common.error'), variant: 'destructive' });
      setUserListModal(prev => ({ ...prev, loading: false }));
    }
  };

  const handleShowFollowing = async () => {
    setUserListModal({ isOpen: true, title: t('profile.following'), users: [], loading: true });
    try {
      const response = await socialAPI.getFollowing(username);
      setUserListModal(prev => ({ ...prev, users: response.data.users, loading: false }));
    } catch (error) {
      toast({ title: t('common.error'), variant: 'destructive' });
      setUserListModal(prev => ({ ...prev, loading: false }));
    }
  };

  if (loading) {
    return (
      <div className="p-6 text-center text-gray-400">{t('common.loading', 'Loading...')}</div>
    );
  }

  if (!profile) {
    return (
      <div className="p-6 text-center text-gray-400">{t('profile.notFound', 'Profile not found')}</div>
    );
  }

  // Filter highlights to only show featured media
  const mediaPosts = profile.posts?.filter(p => (p.video || p.image_url) && p.is_featured) || [];

  return (
    <>
      {/* --- DESKTOP VIEW --- */}
      <div className="hidden lg:block p-0 sm:p-6 max-w-4xl mx-auto">
        {/* Profile Header */}
        <Card className="bg-[#1a1a1b] border-gray-800 mb-0 sm:mb-6 rounded-none sm:rounded-lg">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row items-center md:items-start gap-4 md:gap-6 text-center md:text-left">
              <Avatar className="h-20 w-20 md:h-24 md:w-24">
                <AvatarImage src={profile.avatar_url} />
                <AvatarFallback className="bg-blue-600 text-2xl">
                  {profile.username?.substring(0, 2).toUpperCase()}
                </AvatarFallback>
              </Avatar>

              <div className="flex-1 w-full">
                <div className="flex flex-col md:flex-row items-center gap-3 mb-3">
                  <h1 className="text-2xl font-bold text-white">
                    {profile.display_name || profile.username}
                  </h1>
                  {isOwnProfile ? (
                    <div className="flex items-center justify-center gap-2 w-full md:w-auto mt-2 md:mt-0">
                      <Link to="/profile/me/edit" className="flex-none">
                        <Button variant="outline" size="icon" className="h-9 w-9 text-gray-400 hover:text-white" title={t('common.edit')}>
                          <Edit className="h-4 w-4" />
                        </Button>
                      </Link>
                    </div>
                  ) : (
                    <div className="flex flex-col sm:flex-row items-center justify-center gap-2 w-full md:w-auto mt-2 md:mt-0">
                      <Button
                        variant={profile.is_followed_by_me ? "outline" : "default"}
                        size="sm"
                        onClick={handleFollow}
                        disabled={followLoading}
                        className={`w-full sm:w-auto ${profile.is_followed_by_me ? "" : "bg-blue-600 hover:bg-blue-700"}`}
                      >
                        {profile.is_followed_by_me ? (
                          <><UserMinus className="h-4 w-4 mr-1" /> {t('profile.unfollow')}</>
                        ) : (
                          <><UserPlus className="h-4 w-4 mr-1" /> {t('profile.follow')}</>
                        )}
                      </Button>
                      <Link to={`/messages/${profile.user_id}`} className="w-full sm:w-auto">
                        <Button variant="secondary" size="sm" className="w-full">
                          <MessageCircle className="h-4 w-4 mr-1" /> Mesaj G├Ânder
                        </Button>
                      </Link>
                    </div>
                  )}
                </div>

                {/* Follower/Following counts */}
                <div className="flex justify-center md:justify-start gap-4 mb-3 text-sm flex-wrap">
                  <span className="text-gray-300">
                    <span className="font-bold text-white">{profile.follower_count || 0}</span> {t('profile.followers')}
                  </span>
                  <span className="text-gray-300">
                    <span className="font-bold text-white">{profile.following_count || 0}</span> {t('profile.following')}
                  </span>
                </div>

                {/* Positions */}
                {profile.role !== 'standard' && profile.positions?.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-3">
                    {profile.positions.map((pos, i) => (
                      <Badge key={i} className="bg-blue-600">{pos}</Badge>
                    ))}
                  </div>
                )}

                {/* Bio */}
                {profile.bio && (
                  <p className="text-gray-300 mb-3">{profile.bio}</p>
                )}

                {/* Badges Section */}
                {profile.badges && profile.badges.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2 justify-center md:justify-start">
                    {profile.badges.map(badge => (
                      <div key={badge.id} className="group relative">
                        <div className="bg-gradient-to-r from-yellow-500/20 to-orange-500/20 border border-yellow-500/40 rounded-full px-3 py-1 flex items-center gap-1.5 cursor-help transition-all hover:bg-yellow-500/30">
                          <Trophy className="w-3.5 h-3.5 text-yellow-400" />
                          <span className="text-yellow-200 text-xs font-semibold">
                            {badge.badge_type === 'play_of_week' ? t('profile.playOfWeek') : t('profile.mvp')}
                          </span>
                        </div>
                        {/* Badge Tooltip */}
                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-48 bg-[#1a1a1b] border border-gray-700 rounded-lg p-3 shadow-xl opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
                          <p className="text-white text-xs font-bold mb-1">
                            {badge.badge_type === 'play_of_week' ? t('profile.playOfWeekWinner') : t('profile.mvpAward')}
                          </p>
                          <p className="text-gray-400 text-[10px]">
                            {badge.metadata?.like_count} {t('profile.wonWithLikes')}
                            <br />
                            {new Date(badge.earned_at).toLocaleDateString()}
                          </p>
                          <div className="absolute -bottom-1.5 left-1/2 -translate-x-1/2 w-3 h-3 bg-[#1a1a1b] border-b border-r border-gray-700 transform rotate-45"></div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Stats */}
                <div className="mt-4 flex flex-wrap gap-4 text-sm text-gray-400 justify-center md:justify-start">
                  {profile.location && (
                    <span className="flex items-center gap-1">
                      <MapPin className="h-4 w-4" />
                      {profile.location}
                    </span>
                  )}
                  {profile.role !== 'standard' && profile.height_cm && (
                    <span className="flex items-center gap-1">
                      <Ruler className="h-4 w-4" />
                      {profile.height_cm} cm
                    </span>
                  )}
                  {profile.role !== 'standard' && profile.weight_kg && (
                    <span className="flex items-center gap-1">
                      <WeightIcon className="h-4 w-4" />
                      {profile.weight_kg} kg
                    </span>
                  )}
                  {(profile.role === 'standard' || profile.role === 'admin') && (profile.favorite_team_id || profile.favorite_team) && (
                    <div className="flex items-center gap-2 px-3 py-1 bg-blue-600 border border-blue-400/50 rounded-full shadow-lg shadow-blue-500/30 group hover:scale-105 transition-all cursor-default">
                      <div className="bg-white/20 p-0.5 rounded">
                        {profile.favorite_team_logo ? (
                          <img
                            src={profile.favorite_team_logo}
                            alt=""
                            className="h-4 w-4 object-contain rounded-sm"
                          />
                        ) : (
                          <Shield className="h-4 w-4 text-white" />
                        )}
                      </div>
                      <span className="text-sm font-bold text-white tracking-wide">
                        {profile.favorite_team_name || profile.favorite_team}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Experiences - Hide for standard users */}
        {profile.role !== 'standard' && (
          <Card className="bg-[#1a1a1b] border-gray-800 mb-6">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Trophy className="h-5 w-5" />
                {t('profile.experience')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {profile.experiences?.length > 0 ? (
                <div className="space-y-4">
                  {profile.experiences.map((exp) => (
                    <div key={exp.id} className="border-l-2 border-blue-600 pl-4">
                      <h3 className="text-white font-medium">{exp.team}</h3>
                      <p className="text-gray-400 text-sm">
                        {exp.position && <span>{exp.position} ÔÇó </span>}
                        {exp.league && <span>{exp.league} ÔÇó </span>}
                        {exp.season && <span>{exp.season}</span>}
                      </p>
                      {exp.achievements && (
                        <p className="text-gray-300 text-sm mt-1">{exp.achievements}</p>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-400">{t('profile.noExperience', 'No experience added yet')}</p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Favorite Players */}
        {(profile.role === 'standard' || profile.role === 'admin') && profile.favorite_players?.length > 0 && (
          <Card className="bg-[#1a1a1b] border-gray-800 mb-6">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Users className="h-5 w-5 text-blue-500" />
                {t('profile.favoritePlayers')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {profile.favorite_players.map(player => (
                  <Link key={player.id} to={`/profile/${player.username}`}>
                    <div className="flex items-center gap-3 p-3 bg-[#2a2a2b] rounded-lg hover:bg-[#323233] transition-colors border border-gray-700/50 group">
                      <Avatar className="h-10 w-10 border border-gray-700">
                        <AvatarImage src={player.avatar_url} />
                        <AvatarFallback className="bg-blue-600/20 text-blue-400">{player.username?.[0]?.toUpperCase()}</AvatarFallback>
                      </Avatar>
                      <div className="overflow-hidden">
                        <p className="text-white font-medium truncate group-hover:text-blue-400 transition-colors">
                          {player.display_name || player.username}
                        </p>
                        <p className="text-gray-400 text-xs truncate">@{player.username}</p>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tabs Switcher */}
        <div className="flex border-b border-gray-800 mb-6 bg-[#0a0a0b] sticky top-[56px] lg:top-0 z-10 backdrop-blur-sm sm:static w-full overflow-x-auto scrollbar-hide">
          {profile.membership_type === 'seller' ? (
            <button
              onClick={() => setActiveTab('products')}
              className={`flex-1 min-w-[120px] px-2 md:px-6 py-4 text-sm font-bold transition-all ${activeTab === 'products' ? 'text-emerald-500 border-b-2 border-emerald-500' : 'text-gray-400 hover:text-white'}`}
            >
              <Store className="w-4 h-4 inline mr-2" />
              {t('marketplace.products', '├£r├╝nler')}
            </button>
          ) : (
            <button
              onClick={() => setActiveTab('highlights')}
              className={`flex-1 min-w-[120px] px-2 md:px-6 py-4 text-sm font-bold transition-all ${activeTab === 'highlights' ? 'text-blue-500 border-b-2 border-blue-500' : 'text-gray-400 hover:text-white'}`}
            >
              {t('profile.highlights')}
            </button>
          )}
          <button
            onClick={() => setActiveTab('feed')}
            className={`flex-1 min-w-[120px] px-2 md:px-6 py-4 text-sm font-bold transition-all ${activeTab === 'feed' ? 'text-blue-500 border-b-2 border-blue-500' : 'text-gray-400 hover:text-white'}`}
          >
            {t('profile.posts')}
          </button>
        </div>

        <div className="px-4 sm:px-0">
          {activeTab === 'products' ? (
            /* Products grid for sellers */
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {userListings.length > 0 ? (
                userListings.map((listing) => (
                  <div key={listing.id} className="bg-[#1a1a1b] border border-gray-800 rounded-xl overflow-hidden hover:border-emerald-500/30 transition-colors">
                    <div className="h-32 bg-gradient-to-br from-gray-800 to-gray-900 flex items-center justify-center">
                      {listing.image_urls && listing.image_urls.length > 0 ? (
                        <img src={listing.image_urls[0]} alt={listing.title} className="w-full h-full object-cover" />
                      ) : (
                        <Package className="w-10 h-10 text-gray-700" />
                      )}
                    </div>
                    <div className="p-3">
                      <h3 className="text-white text-sm font-medium truncate">{listing.title}</h3>
                      <p className="text-emerald-400 font-bold text-sm mt-1">
                        {listing.price.toLocaleString('tr-TR')} {listing.currency}
                      </p>
                      <span className="text-[10px] text-gray-500 uppercase">{t(`marketplace.categories.${listing.category}`)}</span>
                    </div>
                  </div>
                ))
              ) : (
                <div className="col-span-full py-12 text-center text-gray-500">
                  <Store className="h-12 w-12 mx-auto mb-3 opacity-20" />
                  {t('marketplace.noListings')}
                </div>
              )}
            </div>
          ) : activeTab === 'highlights' ? (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 sm:gap-4">
              {mediaPosts.length > 0 ? (
                mediaPosts.map((post) => (
                  <div key={post.id} className="relative group">
                    <Link to={`/post/${post.id}`}>
                      <div className="relative aspect-video bg-[#0a0a0b] rounded-lg overflow-hidden border border-gray-800 hover:border-blue-500/50 transition-all duration-300">
                        {post.video?.thumbnail_url ? (
                          <img
                            src={post.video.thumbnail_url}
                            alt=""
                            className="w-full h-full object-cover"
                          />
                        ) : post.video ? (
                          <div className="w-full h-full flex items-center justify-center">
                            <Play className="h-8 w-8 text-blue-500" />
                          </div>
                        ) : (
                          <img
                            src={post.image_url}
                            alt=""
                            className="w-full h-full object-cover"
                          />
                        )}
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-2">
                          <p className="text-white text-[10px] font-medium truncate">{post.caption || t('profile.highlight')}</p>
                        </div>
                      </div>
                    </Link>
                    {(isOwnProfile || user?.role === 'admin') && (
                      <Button
                        variant="destructive"
                        size="icon"
                        className="absolute top-2 right-2 h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={(e) => {
                          e.preventDefault();
                          handleDeletePost(post.id);
                        }}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                ))
              ) : (
                <div className="col-span-full py-12 text-center text-gray-500">
                  <Play className="h-12 w-12 mx-auto mb-3 opacity-20" />
                  {t('profile.noHighlights')}
                </div>
              )}
            </div>
          ) : (
            <div className="max-w-2xl mx-auto py-2">
              {profile.posts?.length > 0 ? (
                profile.posts.map(post => (
                  <PostCard
                    key={post.id}
                    post={post}
                    onLike={handleLike}
                    onComment={handleComment}
                    onDeleteComment={handleDeleteComment}
                    onVote={handleVote}
                    onDelete={handleDeletePost}
                    onEdit={handleEditPost}
                    onReport={handleReportPost}
                    onFeature={handleFeaturePost}
                  />
                ))
              ) : (
                <div className="py-12 text-center text-gray-500">
                  {t('profile.noPosts')}
                </div>
              )}
            </div>
          )}
        </div>

      </div>

      {/* --- MOBILE VIEW --- */}
      <div className="block lg:hidden min-h-screen bg-[#0a0a0b] text-white">
        {/* Sticky Mobile Header */}
        <div className="lg:hidden sticky top-0 z-50 bg-[#1a1a1b]/60 backdrop-blur-xl border-b border-white/5 px-4 h-16 flex items-center justify-between">
          <button
            onClick={() => navigate(-1)}
            className="p-2 -ml-2 text-gray-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
          <h2 className="text-lg font-black uppercase tracking-tight italic">
            {profile.display_name || profile.username}
          </h2>
          <div className="w-6 h-6 invisible" /> {/* Spacer */}
        </div>

        <div className="p-4 sm:p-6 max-w-4xl mx-auto space-y-6 pb-24">
          {/* Profile Hero Section */}
          <div className="bg-[#1a1a1b]/60 backdrop-blur-xl p-8 rounded-[2.5rem] border border-white/5 relative overflow-hidden group">
            <div className="absolute top-0 right-0 w-64 h-64 bg-snapp-blue/5 blur-[80px] -mr-32 -mt-32" />

            <div className="relative z-10 flex flex-col items-center text-center">
              {/* Avatar Section */}
              <div className="relative mb-6">
                <div className="absolute inset-0 bg-gradient-to-br from-snapp-purple to-snapp-blue rounded-[2.5rem] blur-xl opacity-20 animate-pulse" />
                <div className="relative p-1 bg-white/5 backdrop-blur-md rounded-[2.5rem] border border-white/10 shadow-2xl">
                  <Avatar className="h-28 w-28 md:h-32 md:w-32 rounded-[2.2rem]">
                    <AvatarImage src={profile.avatar_url} className="object-cover" />
                    <AvatarFallback className="bg-gradient-to-br from-snapp-purple to-snapp-blue text-3xl font-black text-white">
                      {profile.username?.substring(0, 2).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  {isOwnProfile && (
                    <Link
                      to="/profile/me/edit"
                      className="absolute bottom-0 right-0 p-2 bg-snapp-purple text-white rounded-2xl shadow-lg ring-4 ring-[#1a1a1b] hover:scale-110 transition-all active:scale-95"
                    >
                      <Camera className="w-4 h-4" />
                    </Link>
                  )}
                </div>
              </div>

              {/* Name & Bio */}
              <div className="mb-8">
                <h1 className="text-3xl font-black text-white uppercase tracking-tight flex items-center justify-center gap-3 mb-2">
                  {profile.display_name || profile.username}
                  {profile.is_private && <Lock className="h-5 w-5 text-gray-500" />}
                </h1>
                <p className="text-sm font-black text-snapp-blue uppercase tracking-[0.2em] italic mb-4">
                  @{profile.username}
                </p>
                {profile.bio && (
                  <p className="text-gray-400 font-medium max-w-lg leading-relaxed">
                    {profile.bio}
                  </p>
                )}
              </div>

              {/* Follow Progress / Stats */}
              <div className="flex gap-8 mb-8">
                <button onClick={handleShowFollowers} className="flex flex-col items-center group transition-all">
                  <span className="text-2xl font-black text-white tabular-nums group-hover:text-snapp-purple transition-colors">
                    {profile.follower_count || 0}
                  </span>
                  <span className="text-[10px] font-black text-gray-500 uppercase tracking-widest italic group-hover:text-gray-400">
                    {t('profile.followers')}
                  </span>
                </button>
                <div className="w-px h-8 bg-white/5 mt-2" />
                <button onClick={handleShowFollowing} className="flex flex-col items-center group transition-all">
                  <span className="text-2xl font-black text-white tabular-nums group-hover:text-snapp-blue transition-colors">
                    {profile.following_count || 0}
                  </span>
                  <span className="text-[10px] font-black text-gray-500 uppercase tracking-widest italic group-hover:text-gray-400">
                    {t('profile.following')}
                  </span>
                </button>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 w-full max-w-sm">
                {isOwnProfile ? (
                  <Link to="/profile/me/edit" className="flex-1">
                    <Button className="w-full h-14 bg-white/5 hover:bg-white/10 text-white font-black rounded-2xl border border-white/5 transition-all active:scale-95">
                      <Edit className="w-5 h-5 mr-2" />
                      {t('profile.edit')}
                    </Button>
                  </Link>
                ) : (
                  <>
                    <Button
                      onClick={handleFollow}
                      disabled={followLoading}
                      className={cn(
                        "flex-1 h-14 font-black rounded-2xl transition-all active:scale-95 shadow-xl",
                        profile.follow_status === 'following'
                          ? "bg-white/5 hover:bg-white/10 text-white border border-white/5"
                          : "bg-gradient-to-br from-snapp-purple to-snapp-blue text-white shadow-snapp-purple/20"
                      )}
                    >
                      {profile.follow_status === 'following' ? (
                        <><UserMinus className="h-5 w-5 mr-2" /> {t('profile.unfollow')}</>
                      ) : (
                        <><UserPlus className="h-5 w-5 mr-2" /> {t('profile.follow')}</>
                      )}
                    </Button>
                    <Link to={`/messages/${profile.user_id}`} className="flex-1">
                      <Button className="w-full h-14 bg-white/5 hover:bg-white/10 text-white font-black rounded-2xl border border-white/5 transition-all active:scale-95">
                        <MessageCircle className="w-5 h-5 mr-2" />
                        {t('profile.message')}
                      </Button>
                    </Link>
                  </>
                )}
              </div>
            </div>

            {/* Detailed Stats Row */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-12 pt-8 border-t border-white/5">
              {profile.location && (
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-snapp-blue/10 rounded-xl">
                    <MapPin className="w-4 h-4 text-snapp-blue" />
                  </div>
                  <div className="flex flex-col">
                    <span className="text-[10px] font-black text-gray-500 uppercase tracking-widest italic">Konum</span>
                    <span className="text-sm font-bold text-white truncate">{profile.location}</span>
                  </div>
                </div>
              )}
              {profile.role !== 'standard' && profile.height_cm && (
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-snapp-purple/10 rounded-xl">
                    <Ruler className="w-4 h-4 text-snapp-purple" />
                  </div>
                  <div className="flex flex-col">
                    <span className="text-[10px] font-black text-gray-500 uppercase tracking-widest italic">Boy</span>
                    <span className="text-sm font-bold text-white">{profile.height_cm} cm</span>
                  </div>
                </div>
              )}
              {profile.role !== 'standard' && profile.weight_kg && (
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-emerald-500/10 rounded-xl">
                    <WeightIcon className="w-4 h-4 text-emerald-500" />
                  </div>
                  <div className="flex flex-col">
                    <span className="text-[10px] font-black text-gray-500 uppercase tracking-widest italic">Kilo</span>
                    <span className="text-sm font-bold text-white">{profile.weight_kg} kg</span>
                  </div>
                </div>
              )}
              {(profile.favorite_team_name || profile.favorite_team) && (
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-amber-500/10 rounded-xl">
                    <Shield className="w-4 h-4 text-amber-500" />
                  </div>
                  <div className="flex flex-col">
                    <span className="text-[10px] font-black text-gray-500 uppercase tracking-widest italic">Takım</span>
                    <span className="text-sm font-bold text-white truncate">{profile.favorite_team_name || profile.favorite_team}</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Badges Carousel (If any) */}
          {profile.badges && profile.badges.length > 0 && (
            <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide">
              {profile.badges.map(badge => (
                <div key={badge.id} className="flex-none bg-gradient-to-br from-yellow-500/20 to-amber-600/20 border border-yellow-500/20 px-6 py-3 rounded-2xl flex items-center gap-3 ring-1 ring-yellow-500/10">
                  <Trophy className="w-5 h-5 text-yellow-500" />
                  <div className="flex flex-col">
                    <span className="text-xs font-black text-white uppercase tracking-tight">
                      {badge.badge_type === 'play_of_week' ? t('profile.playOfWeek') : t('profile.mvp')}
                    </span>
                    <span className="text-[10px] font-bold text-yellow-500/70 uppercase">
                      {new Date(badge.earned_at).getFullYear()} Winner
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Experiences */}
          {profile.role !== 'standard' && profile.experiences?.length > 0 && (
            <div className="space-y-4">
              <div className="flex items-center gap-3 px-2">
                <div className="p-2 bg-snapp-purple/10 rounded-xl">
                  <Trophy className="w-5 h-5 text-snapp-purple" />
                </div>
                <h3 className="text-xl font-black text-white uppercase tracking-tight">{t('profile.experience')}</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {profile.experiences.map((exp) => (
                  <div key={exp.id} className="bg-[#1a1a1b]/60 backdrop-blur-xl p-6 rounded-[2rem] border border-white/5 hover:border-snapp-purple/30 transition-all group">
                    <div className="flex items-center gap-4">
                      <div className="p-3 bg-white/5 rounded-2xl shadow-inner group-hover:bg-snapp-purple/10 transition-colors">
                        <Trophy className="w-6 h-6 text-snapp-purple" />
                      </div>
                      <div>
                        <h4 className="text-lg font-black text-white uppercase tracking-tight">{exp.team}</h4>
                        <p className="text-xs font-black text-gray-500 uppercase tracking-widest italic">
                          {exp.season} {exp.league && `• ${exp.league}`}
                        </p>
                      </div>
                    </div>
                    {exp.achievements && (
                      <div className="mt-4 p-3 bg-white/5 rounded-xl border border-white/5 italic text-sm text-gray-400 font-medium">
                        "{exp.achievements}"
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Favorite Players Component Match */}
          {(profile.favorite_players?.length > 0) && (
            <div className="space-y-4">
              <div className="flex items-center gap-3 px-2">
                <div className="p-2 bg-snapp-blue/10 rounded-xl">
                  <Users className="w-5 h-5 text-snapp-blue" />
                </div>
                <h3 className="text-xl font-black text-white uppercase tracking-tight">{t('profile.favoritePlayers')}</h3>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {profile.favorite_players.map(player => (
                  <Link key={player.id} to={`/profile/${player.username}`} className="block">
                    <div className="flex items-center gap-4 bg-[#1a1a1b]/60 backdrop-blur-xl p-4 rounded-[1.5rem] border border-white/5 hover:border-snapp-blue/30 transition-all group active:scale-95">
                      <Avatar className="h-12 w-12 rounded-xl ring-1 ring-white/10 group-hover:ring-snapp-blue/50 transition-all">
                        <AvatarImage src={player.avatar_url} className="rounded-xl" />
                        <AvatarFallback className="bg-snapp-blue text-white font-black rounded-xl">
                          {player.username?.[0]?.toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <div className="overflow-hidden">
                        <p className="font-black text-white text-sm uppercase tracking-tight truncate group-hover:text-snapp-blue transition-colors">
                          {player.display_name || player.username}
                        </p>
                        <p className="text-[10px] font-black text-gray-500 uppercase tracking-widest italic truncate">@{player.username}</p>
                      </div>
                      <ChevronRight className="w-4 h-4 text-gray-700 ml-auto group-hover:text-snapp-blue group-hover:translate-x-1 transition-all" />
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Tabs Switcher - Sticky */}
          <div className="sticky top-16 lg:top-0 z-40 bg-[#0a0a0b]/80 backdrop-blur-xl py-4 -mx-4 px-4 sm:mx-0 sm:px-0">
            <Tabs activeTab={activeTab} onTabChange={setActiveTab} profile={profile} />
          </div>

          {/* Content Section */}
          <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
            {activeTab === 'feed' && (profile.is_private && !isOwnProfile && profile.follow_status !== 'following') && (
              <div className="flex flex-col items-center justify-center py-20 text-center bg-[#1a1a1b] rounded-lg border border-gray-800">
                <div className="bg-gray-800/50 p-6 rounded-full mb-4">
                  <Lock className="h-12 w-12 text-gray-500" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">{t('profile.privateAccount', 'Bu Hesap Gizli')}</h3>
                <p className="text-gray-400 max-w-xs mx-auto">
                  {t('profile.privateAccountDesc', 'Paylaşımları görmek için bu hesabı takip etmelisiniz.')}
                </p>
              </div>
            )}

            {activeTab === 'products' ? (
              /* Products grid for sellers */
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {userListings.length > 0 ? (
                  userListings.map((listing) => (
                    <div key={listing.id} className="bg-[#1a1a1b] border border-gray-800 rounded-xl overflow-hidden hover:border-emerald-500/30 transition-colors">
                      <div className="h-32 bg-gradient-to-br from-gray-800 to-gray-900 flex items-center justify-center">
                        {listing.image_urls && listing.image_urls.length > 0 ? (
                          <img src={listing.image_urls[0]} alt={listing.title} className="w-full h-full object-cover" />
                        ) : (
                          <Package className="w-10 h-10 text-gray-700" />
                        )}
                      </div>
                      <div className="p-3">
                        <h3 className="text-white text-sm font-medium truncate">{listing.title}</h3>
                        <p className="text-emerald-400 font-bold text-sm mt-1">
                          {listing.price.toLocaleString('tr-TR')} {listing.currency}
                        </p>
                        <span className="text-[10px] text-gray-500 uppercase">{t(`marketplace.categories.${listing.category}`)}</span>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="col-span-full py-12 text-center text-gray-500">
                    <Store className="h-12 w-12 mx-auto mb-3 opacity-20" />
                    {t('marketplace.noListings')}
                  </div>
                )}
              </div>
            ) : activeTab === 'highlights' ? (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2 sm:gap-4">
                {mediaPosts.length > 0 ? (
                  mediaPosts.map((post) => (
                    <div key={post.id} className="relative group">
                      <Link to={`/post/${post.id}`}>
                        <div className="relative aspect-video bg-[#0a0a0b] rounded-lg overflow-hidden border border-gray-800 hover:border-blue-500/50 transition-all duration-300">
                          {post.video?.thumbnail_url ? (
                            <img
                              src={post.video.thumbnail_url}
                              alt=""
                              className="w-full h-full object-cover"
                            />
                          ) : post.video ? (
                            <div className="w-full h-full flex items-center justify-center">
                              <Play className="h-8 w-8 text-blue-500" />
                            </div>
                          ) : (
                            <img
                              src={post.image_url}
                              alt=""
                              className="w-full h-full object-cover"
                            />
                          )}
                          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-2">
                            <p className="text-white text-[10px] font-medium truncate">{post.caption || t('profile.highlight')}</p>
                          </div>
                        </div>
                      </Link>
                      {(isOwnProfile || user?.role === 'admin') && (
                        <Button
                          variant="destructive"
                          size="icon"
                          className="absolute top-2 right-2 h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity"
                          onClick={(e) => {
                            e.preventDefault();
                            handleDeletePost(post.id);
                          }}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                  ))
                ) : (
                  <div className="col-span-full py-12 text-center text-gray-500">
                    <Play className="h-12 w-12 mx-auto mb-3 opacity-20" />
                    {t('profile.noHighlights')}
                  </div>
                )}
              </div>
            ) : activeTab === 'feed' ? (
              <div className="max-w-2xl mx-auto py-2">
                {(!profile.is_private || isOwnProfile || profile.follow_status === 'following') ? (
                  profile.posts?.length > 0 ? (
                    profile.posts.map(post => (
                      <PostCard
                        key={post.id}
                        post={post}
                        onLike={handleLike}
                        onComment={handleComment}
                        onDeleteComment={handleDeleteComment}
                        onVote={handleVote}
                        onDelete={handleDeletePost}
                        onEdit={handleEditPost}
                        onReport={handleReportPost}
                        onFeature={handleFeaturePost}
                      />
                    ))
                  ) : (
                    <div className="py-12 text-center text-gray-500">
                      {t('profile.noPosts')}
                    </div>
                  )
                ) : null}
              </div>
            ) : null}
          </div>
        </div>

      </div>

      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        onClose={() => setDeleteConfirm({ isOpen: false, postId: null })}
        onConfirm={confirmDelete}
        title={t('feed.deletePostTitle')}
        description={t('feed.deletePostText')}
      />

      <UserListModal
        isOpen={userListModal.isOpen}
        onClose={() => setUserListModal(prev => ({ ...prev, isOpen: false }))}
        title={userListModal.title}
        users={userListModal.users}
        loading={userListModal.loading}
      />
    </>
  );
};

export default Profile;
